<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/apiService";
import BackendPagination from "@/components/BackendPagination.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import truncate from "html-truncate";

const imgUrl = process.env.VUE_APP_API;

const blogs = ref([]);
const loading = ref(true);
const errorMessage = ref("");
const currentPage = ref(1);
const totalItems = ref(0);
const itemsPerPage = 10;

const totalPages = computed(() =>
    Math.ceil(totalItems.value / itemsPerPage)
);

const router = useRouter();

const fetchBlogs = async () => {
    loading.value = true;
    try {
        const response = await api.get(`/blog_posts?page=${currentPage.value}`);
        blogs.value = response.data.member || [];
        totalItems.value = response.data.totalItems || 0;
    } catch (error) {
        errorMessage.value = error.response?.data?.message || "Error al obtener los blogs.";
    } finally {
        loading.value = false;
    }
};

const changePage = async (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        await fetchBlogs();
    }
};

onMounted(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
        router.push("/signin");
    } else {
        fetchBlogs();
    }
});

const truncateHtmlContent = (html, limit = 30) => {
    return truncate(html, limit, { ellipsis: "..." });
};

const deleteBlog = async (id) => {
    const confirmDelete = confirm("¿Estás seguro de que deseas eliminar este blog?");
    if (!confirmDelete) return;

    try {
        await api.delete(`/blog_posts/${id}`);
        blogs.value = blogs.value.filter(blog => blog.id !== id); // Remove from UI
    } catch (error) {
        errorMessage.value = "Error al eliminar el blog.";
    }
};
</script>

<template>
    <div class="container-fluid py-4">
        <div class="pb-0 d-flex mb-3">
            <h6>Blogs</h6>
            <argon-button color="primary" size="sm" class="ms-auto" @click="router.push('/crearblog')">
                <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
                Agregar blog
            </argon-button>
        </div>

        <div v-if="loading" class="text-center text-secondary py-5">
            Cargando blogs...
        </div>

        <div v-if="errorMessage" class="alert alert-danger text-dark">
            {{ errorMessage }}
        </div>

        <div class="row">
            <div v-for="blog in blogs" :key="blog.id" class="col-12 col-md-6 col-lg-4 mb-4">
                <div class="card h-90 w-90 shadow-sm">
                    <div class="ratio ratio-16x9 overflow-hidden imageBlog">
                        <img v-if="!blog.imageError" :src="`${imgUrl}/uploads/images/${blog.imageName}`" alt="Thumbnail"
                            class="w-100 h-100 object-fit-cover" @error="blog.imageError = true" />
                        <div v-else
                            class="w-100 h-100 object-fit-cover bg-light d-flex align-items-center justify-content-center text-muted">
                            📷 No picture
                        </div>
                    </div>

                    <div class="card-body d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-start">
                            <h6 class="card-title text-primary mb-0">
                                {{ blog.title.length > 30 ? blog.title.slice(0, 30) + '...' : blog.title }}
                            </h6>

                        </div>

                        <p class="card-text text-muted flex-grow-1" v-html="truncateHtmlContent(blog.content, 30)" />

                        <small class="text-muted mt-2">
                            Publicado el {{ new Date(blog.publicationDate).toLocaleDateString() }}
                        </small>
                        <div class="d-flex justify-content-between">

                            <router-link :to="`/blogdetails/${blog.id}`"
                                class="text-primary text-decoration-none fw-semibold d-inline-flex align-items-center gap-1 mt-1">
                                Leer más
                                <font-awesome-icon icon="arrow-right" />
                            </router-link>

                            <font-awesome-icon icon="trash" class="mt-2" @click="deleteBlog(blog.id)" />

                        </div>
                    </div>
                </div>
            </div>
        </div>

        <BackendPagination :current-page="currentPage" :total-pages="totalPages" @change-page="changePage" />
    </div>
</template>

<style scoped>
.card-title {
    font-weight: 600;
}

.card-text {
    font-size: 0.9rem;
}

.imageBlog {
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
}
</style>
